import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import {
  OCCUPANCY,
  RATE,
} from 'lumen/utils/constants/short-term-rental-constants';

export default class ShortTermRentalKpi extends Model {
  [Type] = 'short-term-rental-kpi' as const;

  @attr('number') declare difference: number;
  @attr('string') declare message: string;
  @attr('string') declare subtype: string;
  @attr('string') declare type: string;
  @attr('number') declare value: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get colorClass() {
    return this.difference > 0
      ? 'text-success-default'
      : 'text-critical-default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChange() {
    if (this.isMissing || this.difference === null) {
      return null;
    }
    if (
      (!this.isOccupancy && Math.round(this.difference) === 0) ||
      (this.isOccupancy && Math.round(this.difference * 100) === 0)
    ) {
      return null;
    }
    const diffText = this.isOccupancy
      ? this.intl.formatNumber(this.difference, {
          style: 'percent',
          signDisplay: 'exceptZero',
        })
      : this.intl.formatNumber(this.difference, { signDisplay: 'exceptZero' });

    return diffText;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDifferenceRate() {
    if (typeof this.difference === 'string') {
      return this.difference;
    }
    if (Math.round(this.difference) === 0) {
      return null;
    }
    return this.hotelAndMarketService.formatRate(this.difference, {
      longFormat: false,
      includeSign: true,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    if (this.isMissing) {
      return this.intl.t('general.missing');
    }
    if (this.isOccupancy) {
      return this.intl.formatNumber(this.value, { style: 'percent' });
    }
    if (this.isRate) {
      return this.hotelAndMarketService.formatRate(this.value, {
        longFormat: false,
      });
    }
    return this.intl.formatNumber(this.value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOccupancy() {
    return this.type === OCCUPANCY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRate() {
    return this.type === RATE;
  }
}
